'use client';

import { useCallback, useEffect, useState } from 'react';
import { useAccount } from 'wagmi';
import useTransactionAdder from '../hooks/useTransactionAdder';
import { useShibaSwapTokenContract, useShiberseLandSaleMainnetContract, useShiberseLandSalePuppynetContract, useShiberseLandSaleShibariumContract, useShibaSwapWethTokenContract, useShiberseLandSaleSeploliaContract, connectSigner, callContractWithRpcGasEstimate } from './useContract';
import { mainNetworkChainId, puppyNetChainId, sepoliaChainId, shibariumChainId, shiberseContractAddresses } from '../constants';
import { Fraction } from '../entities';
import { BigNumber } from '@ethersproject/bignumber';
import { ethers } from 'ethers';

const useShiberseLandAuction = (props: any) => {
    const { address: account } = useAccount();
    const { chain } = useAccount();
    const addTransaction = useTransactionAdder();


    const shibTokenContract = useShibaSwapTokenContract(true);
    const landSalePuppynetContract = useShiberseLandSalePuppynetContract(true);
    const landSaleShibariumContract = useShiberseLandSaleShibariumContract(true);
    const landSaleMainnetContract = useShiberseLandSaleMainnetContract(true);
    const landSaleSeploliaContract = useShiberseLandSaleSeploliaContract(true);
    const wethTokenContract = useShibaSwapWethTokenContract(true);


    const [currentStage, setCurrentStage] = useState(1);

    const [shibTokenAddress, setShibTokenAddress] = useState(
        shiberseContractAddresses[(chain?.id || puppyNetChainId) as keyof typeof shiberseContractAddresses]['SHIB_TOKEN']
    );
    const [wethTokenAddress, setWethTokenAddress] = useState(
        shiberseContractAddresses[(chain?.id || puppyNetChainId) as keyof typeof shiberseContractAddresses]['WETH_TOKEN']
    );
    const [shibAllowance, setShibAllowance] = useState('0');
    const [wethAllowance, setWethAllowance] = useState('0');

    const fetchShibAllowance = useCallback(async () => {
        if (account && shibTokenContract) {
            const getContractAddress = () => {
                if (landSaleMainnetContract && chain?.id == mainNetworkChainId) {
                    return landSaleMainnetContract.address;
                } else if (landSaleSeploliaContract && chain?.id == sepoliaChainId) {
                    return landSaleSeploliaContract.address;
                } else if (landSaleShibariumContract && chain?.id == shibariumChainId) {
                    return landSaleShibariumContract.address;
                } else if (landSalePuppynetContract && chain?.id == puppyNetChainId) {
                    return landSalePuppynetContract.address;
                }
            };

            try {
                const contractAddress = getContractAddress();
                const allowance = await shibTokenContract.allowance(account, contractAddress);
                const formatted = Fraction.from(BigNumber.from(allowance), BigNumber.from(10).pow(18)).toString();
                if (formatted !== shibAllowance) {
                    setShibAllowance(formatted);
                }
            } catch (e) {
                console.log('fetchShibAllowance occurred error: ', e);
                if (shibAllowance !== '0') {
                    setShibAllowance('0');
                }
            }
        }
    }, [account, shibTokenContract, landSaleMainnetContract, landSalePuppynetContract, landSaleShibariumContract, landSaleSeploliaContract]);


    const fetchWethAllowance = useCallback(async () => {
        if (account && wethTokenContract && (chain?.id == puppyNetChainId || chain?.id == shibariumChainId)) {
            const getContractAddress = () => {
                if (landSaleShibariumContract && chain?.id == shibariumChainId) {
                    return landSaleShibariumContract.address;
                } else if (landSalePuppynetContract && chain?.id == puppyNetChainId) {
                    return landSalePuppynetContract.address;
                }
            };

            try {
                const contractAddress = getContractAddress();
                const allowance = await wethTokenContract.allowance(account, contractAddress);
                const formatted = Fraction.from(BigNumber.from(allowance), BigNumber.from(10).pow(18)).toString();
                if (formatted !== wethAllowance) {
                    setWethAllowance(formatted);
                }
            } catch (e) {
                console.error('fetchWethAllowance occurred error: ', e);
                if (wethAllowance !== '0') {
                    setWethAllowance('0');
                }
            }
        }
    }, [account, wethTokenContract, landSalePuppynetContract, landSaleShibariumContract, chain]);


    useEffect(() => {
        if (account) {
            const fetchAllowances = async () => {
                await fetchShibAllowance();
                await fetchWethAllowance();
            };

            fetchAllowances(); // Initial fetch

            const refreshInterval = setInterval(fetchAllowances, 10000);

            return () => clearInterval(refreshInterval);
        }
    }, [account, shibTokenContract, wethTokenContract, landSalePuppynetContract, landSaleShibariumContract]);

    const shibApprove = useCallback(async () => {
        try {
            const spenderAddress = chain?.id == mainNetworkChainId 
                ? landSaleMainnetContract?.address 
                : chain?.id == sepoliaChainId 
                    ? landSaleSeploliaContract?.address 
                    : chain?.id == shibariumChainId 
                        ? landSaleShibariumContract?.address 
                        : landSalePuppynetContract?.address;
            
            // Use the public RPC for gas estimation to avoid MetaMask rate limiting
            const tx = await callContractWithRpcGasEstimate(
                shibTokenContract,
                'approve',
                [spenderAddress, ethers.MaxUint256.toString()],
                {}
            );
            addTransaction(tx, { summary: 'Approve' });
            return { success: true, txHash: tx.hash };
        } catch (e: any) {
            console.error('Approval error:', e);
            return { success: false, error: e.message || 'An error occurred' };
        }
    }, [addTransaction, landSaleMainnetContract, shibTokenContract, landSalePuppynetContract, landSaleShibariumContract, landSaleSeploliaContract, chain]);

    const wethApprove = useCallback(async () => {
        try {
            const spenderAddress = chain?.id == shibariumChainId 
                ? landSaleShibariumContract?.address 
                : landSalePuppynetContract?.address;
            
            // Use the public RPC for gas estimation to avoid MetaMask rate limiting
            const tx = await callContractWithRpcGasEstimate(
                wethTokenContract,
                'approve',
                [spenderAddress, ethers.MaxUint256.toString()],
                {}
            );
            addTransaction(tx, { summary: 'Approve' });
            return { success: true, txHash: tx.hash };
        } catch (e: any) {
            console.error('Approval error:', e);
            return { success: false, error: e.message || 'An error occurred' };
        }
    }, [addTransaction, wethTokenContract, landSalePuppynetContract, landSaleShibariumContract, chain]);

    const fetchCurrentStage = useCallback(async () => {
        if (landSaleMainnetContract && landSalePuppynetContract && chain?.id && landSaleShibariumContract && landSaleShibariumContract && landSaleSeploliaContract) {
            try {
                const contract = chain?.id == mainNetworkChainId ? landSaleMainnetContract : chain?.id == shibariumChainId ? landSaleShibariumContract : chain?.id == sepoliaChainId ? landSaleSeploliaContract : landSalePuppynetContract;
                const current = await contract.currentStage();

                setCurrentStage((prevStage) => {
                    const newStage = Number(current);
                    return prevStage !== newStage ? newStage : prevStage;
                });
            } catch (e) {
                console.error('fetch current stage error occurred', e);
            }
        }
    }, [landSaleMainnetContract, landSalePuppynetContract, landSaleShibariumContract, landSaleSeploliaContract, landSaleSeploliaContract]);


    const fetchLandPrice = useCallback(async ({ x: posX, y: posY }: { x: any, y: any }) => {
        if (landSaleMainnetContract && chain?.id == mainNetworkChainId) {
            if ((posX === 0 || posX) && (posY === 0 || posY)) {
                try {
                    const price = await landSaleMainnetContract.getPriceInEth(posX, posY);
                    return price;
                } catch (error) {
                    console.error('Error fetching land price:', error);
                    throw error; // Re-throw the error if needed
                }
            }
        }
        else if (landSaleSeploliaContract && chain?.id == sepoliaChainId) {
            if ((posX === 0 || posX) && (posY === 0 || posY)) {
                try {
                    const price = await landSaleSeploliaContract.getPriceInEth(posX, posY);
                    return price;
                } catch (error) {
                    console.error('Error fetching land price:', error);
                    throw error; // Re-throw the error if needed
                }
            }
        }
        else if (chain?.id == shibariumChainId && landSaleShibariumContract) {
            if ((posX === 0 || posX) && (posY === 0 || posY)) {
                try {
                    const price = await landSaleShibariumContract.getPriceInEth(posX, posY);
                    return price;
                } catch (error) {
                    console.error('Error fetching land price in bone:', error);
                    throw error; // Re-throw the error if needed
                }
            }
        }
        else if (chain?.id == puppyNetChainId && landSalePuppynetContract) {
            if ((posX === 0 || posX) && (posY === 0 || posY)) {
                const price = await landSalePuppynetContract.getPriceInEth(posX, posY);
                return price;
            }
        }
        // for unconnected wallet state added fetching land price via puppynet contract
        else {
            if ((posX === 0 || posX) && (posY === 0 || posY) && landSalePuppynetContract) {
                const price = await landSalePuppynetContract.getPriceInEth(posX, posY);
                return price;
            }
        }
    }, [landSaleMainnetContract, landSalePuppynetContract, landSaleShibariumContract, landSaleSeploliaContract]);



    const fetchLandShibPrice = useCallback(async ({ x: posX, y: posY }: { x: any, y: any }) => {
        if (chain?.id == mainNetworkChainId && landSaleMainnetContract) {
            if ((posX === 0 || posX) && (posY === 0 || posY)) {
                const price = await landSaleMainnetContract.getPriceInShib(posX, posY);
                return price;
            }
        }
        else if (chain?.id == sepoliaChainId && landSaleSeploliaContract) {
            if ((posX === 0 || posX) && (posY === 0 || posY)) {
                const price = await landSaleSeploliaContract.getPriceInShib(posX, posY);
                return price;
            }
        }
        else if (chain?.id == shibariumChainId && landSaleShibariumContract) {
            if ((posX === 0 || posX) && (posY === 0 || posY)) {
                const price = await landSaleShibariumContract.getPriceInShib(posX, posY);
                return price;
            }
        }
        else if (chain?.id == puppyNetChainId && landSalePuppynetContract) {
            if ((posX === 0 || posX) && (posY === 0 || posY)) {
                const price = await landSalePuppynetContract.getPriceInShib(posX, posY);
                return price;
            }
        }
        // for unconnected wallet state added fetching land price via puppynet contract
        else {
            if ((posX === 0 || posX) && (posY === 0 || posY) && landSalePuppynetContract) {
                const price = await landSalePuppynetContract.getPriceInShib(posX, posY);
                return price;
            }
        }
    }, [landSaleMainnetContract, landSalePuppynetContract, landSaleShibariumContract, landSaleSeploliaContract]);


    useEffect(() => {
        if (account) {
            fetchCurrentStage();
        }
    }, [account]);


    const mintPublic = useCallback(async (input: any) => {
        if (input) {
            try {
                let tx;
                if (chain?.id == mainNetworkChainId && landSaleMainnetContract) {
                    tx = await callContractWithRpcGasEstimate(
                        landSaleMainnetContract,
                        'mintPublic',
                        [input.x, input.y],
                        {
                        from: account,
                        value: input.messageFee.add(input.amount),
                        }
                    );
                }
                else if (chain?.id == sepoliaChainId && landSaleSeploliaContract) {
                    tx = await callContractWithRpcGasEstimate(
                        landSaleSeploliaContract,
                        'mintPublic',
                        [input.x, input.y],
                        {
                        from: account,
                        value: input.messageFee.add(input.amount),
                        }
                    );
                } else if (chain?.id == shibariumChainId && landSaleShibariumContract) {
                    tx = await callContractWithRpcGasEstimate(
                        landSaleShibariumContract,
                        'mintPublic',
                        [input.x, input.y],
                        {}
                    );
                } else if (chain?.id == puppyNetChainId && landSalePuppynetContract) {
                    tx = await callContractWithRpcGasEstimate(
                        landSalePuppynetContract,
                        'mintPublic',
                        [input.x, input.y],
                        {}
                    );
                }
                addTransaction(tx, { summary: `Mint succeed!` });
                return tx;
            } catch (e) {
                return e;
            }
        }
    }, [addTransaction, landSaleMainnetContract, landSalePuppynetContract, landSaleShibariumContract, landSaleSeploliaContract, chain, account]);


    const mintWinningBid = useCallback(async (input: any) => {
        if (input) {
            try {
                let tx;
                if (chain?.id == mainNetworkChainId && landSaleMainnetContract) {
                    const signedContract = connectSigner(landSaleMainnetContract);
                    tx = await signedContract?.mintWinningBid(input.xArray, input.yArray, {
                        from: account,
                        value: input.messageFee,
                    });
                }
                else if (chain?.id == sepoliaChainId && landSaleSeploliaContract) {
                    const signedContract = connectSigner(landSaleSeploliaContract);
                    tx = await signedContract?.mintWinningBid(input.xArray, input.yArray, {
                        from: account,
                        value: input.messageFee,
                    });
                }
                else if (chain?.id == shibariumChainId && landSaleShibariumContract) {
                    const signedContract = connectSigner(landSaleShibariumContract);
                    tx = await signedContract?.mintWinningBid(input.xArray, input.yArray);
                } else if (chain?.id == puppyNetChainId && landSalePuppynetContract) {
                    const signedContract = connectSigner(landSalePuppynetContract);
                    tx = await signedContract?.mintWinningBid(input.xArray, input.yArray);
                }
                addTransaction(tx, { summary: `Mint succeed!` });
                return tx;
            } catch (e) {
                return e;
            }
        }
    }, [addTransaction, landSaleMainnetContract, landSalePuppynetContract, landSaleShibariumContract, landSaleSeploliaContract, chain, account]);

    const mintPublicMulti = useCallback(async (input: any) => {
        if (input) {
            try {
                let tx;
                if (chain?.id == mainNetworkChainId && landSaleMainnetContract) {
                    const signedContract = connectSigner(landSaleMainnetContract);
                    tx = await signedContract?.mintPublicMulti(input.xArray, input.yArray, {
                        from: account,
                        value: input.messageFee.add(input.totalAmount),
                    });
                }
                else if (chain?.id == sepoliaChainId && landSaleSeploliaContract) {
                    const signedContract = connectSigner(landSaleSeploliaContract);
                    tx = await signedContract?.mintPublicMulti(input.xArray, input.yArray, {
                        from: account,
                        value: input.messageFee.add(input.totalAmount),
                    });
                }
                else if (chain?.id == shibariumChainId && landSaleShibariumContract) {
                    const signedContract = connectSigner(landSaleShibariumContract);
                    tx = await signedContract?.mintPublicMulti(input.xArray, input.yArray);
                } else if (chain?.id == puppyNetChainId && landSalePuppynetContract) {
                    const signedContract = connectSigner(landSalePuppynetContract);
                    tx = await signedContract?.mintPublicMulti(input.xArray, input.yArray);
                }
                addTransaction(tx, { summary: `Mint placed!` });
                return tx;
            } catch (e) {
                return e;
            }
        }
    }, [addTransaction, landSaleMainnetContract, landSalePuppynetContract, landSaleShibariumContract, landSaleSeploliaContract, chain, account]);

    const mintPublicWithShib = useCallback(async (input: any) => {
        if (input) {
            try {
                let tx;
                if (chain?.id == mainNetworkChainId && landSaleMainnetContract) {
                    const signedContract = connectSigner(landSaleMainnetContract);
                    tx = await signedContract?.mintPublicWithShib(input.x, input.y, {
                        from: account,
                        value: input.messageFee,
                    });
                }
                else if (chain?.id == sepoliaChainId && landSaleSeploliaContract) {
                    const signedContract = connectSigner(landSaleSeploliaContract);
                    tx = await signedContract?.mintPublicWithShib(input.x, input.y, {
                        from: account,
                        value: input.messageFee,
                    });
                }
                else if (chain?.id == shibariumChainId && landSaleShibariumContract) {
                    const signedContract = connectSigner(landSaleShibariumContract);
                    tx = await signedContract?.mintPublicWithShib(input.x, input.y);
                } else if (chain?.id == puppyNetChainId && landSalePuppynetContract) {
                    const signedContract = connectSigner(landSalePuppynetContract);
                    tx = await signedContract?.mintPublicWithShib(input.x, input.y);
                }
                addTransaction(tx, { summary: `Mint with Shib succeed!` });
                return tx;
            } catch (e) {
                return e;
            }
        }
    }, [addTransaction, landSaleMainnetContract, landSalePuppynetContract, landSaleShibariumContract, landSaleSeploliaContract, account, chain]);

    const mintPublicWithShibMulti = useCallback(async (input: any) => {
        if (input) {
            try {
                let tx;
                if (chain?.id == mainNetworkChainId && landSaleMainnetContract) {
                    const signedContract = connectSigner(landSaleMainnetContract);
                    tx = await signedContract?.mintPublicWithShibMulti(input.xArray, input.yArray, {
                        from: account,
                        value: input.messageFee,
                    });
                }
                else if (chain?.id == sepoliaChainId && landSaleSeploliaContract) {
                    const signedContract = connectSigner(landSaleSeploliaContract);
                    tx = await signedContract?.mintPublicWithShibMulti(input.xArray, input.yArray, {
                        from: account,
                        value: input.messageFee,
                    });
                }
                else if (chain?.id == shibariumChainId && landSaleShibariumContract) {
                    const signedContract = connectSigner(landSaleShibariumContract);
                    tx = await signedContract?.mintPublicWithShibMulti(input.xArray, input.yArray);
                } else if (chain?.id == puppyNetChainId && landSalePuppynetContract) {
                    const signedContract = connectSigner(landSalePuppynetContract);
                    tx = await signedContract?.mintPublicWithShibMulti(input.xArray, input.yArray);
                }
                addTransaction(tx, { summary: `MultiMint with Shib succeed!` });
                return tx;
            } catch (e) {
                return e;
            }
        }
    }, [addTransaction, landSaleMainnetContract, landSalePuppynetContract, landSaleShibariumContract, landSaleSeploliaContract, account, chain]);


    return {
        currentStage,
        mintPublic,
        mintPublicMulti,
        mintPublicWithShib,
        mintPublicWithShibMulti,
        fetchLandPrice,
        fetchLandShibPrice,
        shibTokenAddress,
        wethTokenAddress,
        shibAllowance,
        wethAllowance,
        shibApprove,
        wethApprove,
        fetchWethAllowance,
        mintWinningBid
    };
};

export default useShiberseLandAuction;